import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import ShippingAddressFields from '../components/ShippingAddressFields';
import { getFbCookies } from '../lib/pixel';
import { loadLeadContact } from '../lib/leadContact';
import { useT } from '../i18n';

const EMPTY_SHIPPING = {
  fullName: '', phone: '', city: '', neighborhood: '',
  address: '', landmark: '', postalCode: '',
};

// Upgrade a paid PDF-only order to a printed book: pick a physical variant,
// see the already-paid amount deducted, enter a shipping address, and pay only
// the difference. Mirrors the server's /api/checkout/upgrade-session pricing.
export default function UpgradeOrder() {
  const t = useT();
  const { id } = useParams();
  const { isStaff } = useAuth();

  const [order, setOrder] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedSku, setSelectedSku] = useState(null);
  const [ship, setShip] = useState(() => {
    const lead = loadLeadContact();
    return { ...EMPTY_SHIPPING, fullName: lead?.name || '', phone: lead?.phone || '' };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const o = await api.get(`/api/orders/${id}`);
        if (cancelled) return;
        setOrder(o);
        const token = o.items?.find((it) => it.storyId?.shareToken)?.storyId?.shareToken;
        if (token) {
          const s = await api.get(`/api/stories/preview/${token}`);
          if (!cancelled) setStory(s);
        }
      } catch {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!order) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );

  // Already upgraded → point at the upgrade order instead of charging again.
  if (order.upgradeOrderId) return (
    <div className="upgrade-page">
      <div className="finalize-approved-card">
        <div className="finalize-approved-icon">✓</div>
        <div>
          <strong>{t.upgradeDoneTitle}</strong>
          <p>{t.upgradeDoneDesc}</p>
          <Link to={`/account/orders/${order.upgradeOrderId}`}>{t.upgradeDoneLink} ←</Link>
        </div>
      </div>
    </div>
  );

  const isPaid = order.status !== 'pending_payment' && order.status !== 'cancelled' && order.status !== 'refunded';
  const pdfOnly = (order.items || []).length > 0 && order.items.every((it) => it.type === 'digital');
  if (!isPaid || !pdfOnly) return (
    <div className="error-card">
      <h2>{t.upgradeNotEligible}</h2>
      <Link to={`/account/orders/${order._id}`} className="submit-btn">{t.backToOrder}</Link>
    </div>
  );

  const variantLabels = { softcover: t.softcover, hardcover: t.hardcover };
  const deliveryCents = story?.delivery?.amountCents || 0;
  const creditCents = order.totalCents || 0;
  const options = (story?.template?.variants || [])
    .filter((v) => v.isActive && (v.type === 'softcover' || v.type === 'hardcover'));
  const selected = options.find((v) => v.sku === selectedSku) || options[0] || null;

  const remainingFor = (v) => Math.max(0, v.priceCents + deliveryCents - creditCents);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selected) return;
    setError('');
    setSubmitting(true);
    try {
      const resp = await api.post('/api/checkout/upgrade-session', {
        orderId: order._id,
        variantSku: selected.sku,
        shippingAddress: { ...ship, country: 'إسرائيل' },
        ...getFbCookies(),
        eventSourceUrl: window.location.href,
      });
      window.location.href = resp.sessionUrl;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="upgrade-page">
      <h2>{t.upgradePageTitle}</h2>
      <p className="upgrade-subtitle">{t.upgradePageSubtitle}</p>

      <form className="checkout-form" onSubmit={handleSubmit}>
        <div className="upgrade-options">
          {options.map((v) => (
            <label key={v.sku} className={`upgrade-option ${selected?.sku === v.sku ? 'selected' : ''}`}>
              <input
                type="radio"
                name="upgradeVariant"
                checked={selected?.sku === v.sku}
                onChange={() => setSelectedSku(v.sku)}
              />
              <span className="upgrade-option-name">
                {v.productNameAr || variantLabels[v.type] || v.type}
                {v.includesPdf && <span className="pdf-badge">{t.pdfIncludedTag}</span>}
              </span>
              <span className="upgrade-option-breakdown">
                <span className="upgrade-row">
                  <span>{t.upgradeBookPrice}</span>
                  <span>{(v.priceCents / 100).toFixed(0)}{t.currency}</span>
                </span>
                {deliveryCents > 0 && (
                  <span className="upgrade-row">
                    <span>{t.delivery}</span>
                    <span>{(deliveryCents / 100).toFixed(0)}{t.currency}</span>
                  </span>
                )}
                <span className="upgrade-row upgrade-credit">
                  <span>{t.upgradeAlreadyPaid}</span>
                  <span>−{(creditCents / 100).toFixed(0)}{t.currency}</span>
                </span>
                <span className="upgrade-row upgrade-total">
                  <span>{t.upgradeRemaining}</span>
                  <span>{(remainingFor(v) / 100).toFixed(0)}{t.currency}</span>
                </span>
              </span>
            </label>
          ))}
          {options.length === 0 && <p className="empty-state">{t.upgradeNotEligible}</p>}
        </div>

        {options.length > 0 && (
          <>
            <ShippingAddressFields value={ship} onChange={setShip} />

            {error && <p className="error-text">{error}</p>}

            {isStaff && (
              <p className="admin-free-notice">{t.adminFreeNotice}</p>
            )}

            <button type="submit" className="submit-btn" disabled={submitting || !selected}>
              {submitting
                ? t.checkoutProcessing
                : `${t.upgradePay}${selected ? ` — ${(remainingFor(selected) / 100).toFixed(0)}${t.currency}` : ''}`}
            </button>
          </>
        )}

        <Link to={`/account/orders/${order._id}`} className="upgrade-back-link">{t.backToOrder}</Link>
      </form>
    </div>
  );
}
